import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { CamionMuelle, CargaSalida, CuadroMuelles, SlotMuelle } from '@/types'
import { PageHeader } from '@/components/common/PageHeader'
import { Loader } from '@/components/common/Loader'
import { RefreshButton } from '@/components/common/RefreshButton'
import { useAppStore } from '@/store'
import { useDockTrack } from '@/hooks/useDockTrack'
import { useCargasSinMuelle, useCuadroMuelles } from '@/hooks/useDockSchedule'
import { useCargasSalida } from '@/hooks/useCargasSalida'
import { errorMuelleDe, useDockMutaciones } from '@/hooks/useDockMutaciones'
import {
  MuellesFiltros, FILTRO_CUADRO_VACIO, type FiltroCuadro,
} from '@/components/muelles/MuellesFiltros'
import { MuellesTimeline } from '@/components/muelles/MuellesTimeline'
import { PanelSinMuelle } from '@/components/muelles/PanelSinMuelle'
import { DockKpiStrip } from '@/components/muelles/DockKpiStrip'
import { LeyendaEstados } from '@/components/muelles/LeyendaEstados'
import { CamionDrawer } from '@/components/muelles/CamionDrawer'
import { SlotsDrawer } from '@/components/muelles/SlotsDrawer'
import { SLOT_MINUTOS, ZONA } from '@/config/dockSchedule'
import { estadoCamionDe } from '@/utils/camionesMuelle'
import type { DockVista } from '@/utils/dockTimeTracks'

/** Los almacenes con volumen real, medido sobre las cargas vivas. Los otros dos
 *  (ASIA, RM TRADE) suman cinco cargas entre los dos y no tienen muelles. */
const ALMACENES = ['VID', 'GAVA']

export default function MuellesPage() {
  const { t } = useTranslation()
  const { dark } = useAppStore()
  const [params, setParams] = useSearchParams()

  const almacen = params.get('alm') ?? ALMACENES[0]
  const vista = (params.get('vista') as DockVista) === 'dia' ? 'dia' : 'semana'
  const offset = Number(params.get('off') ?? 0) || 0

  const poner = (clave: string, valor: string) => {
    const p = new URLSearchParams(params)
    if (valor) p.set(clave, valor)
    else p.delete(clave)
    setParams(p, { replace: true })
  }

  const [filtro, setFiltro] = useState<FiltroCuadro>(FILTRO_CUADRO_VACIO)

  // La jornada la manda el backend; hasta que llegue se usa la de arranque para
  // que el eje ya esté pintado y no salte al recibir la respuesta.
  const [jornada, setJornada] = useState({ inicio: 6, fin: 20 })
  const track = useDockTrack(vista, offset, jornada)

  const desde = track.rangeStart.toISOString()
  const hasta = track.rangeEnd.toISOString()

  const cuadroQ = useCuadroMuelles({ desde, hasta, almacen })
  const sinMuelleQ = useCargasSinMuelle(almacen)
  const cargasQ = useCargasSalida({ almacen })
  const mut = useDockMutaciones(almacen)

  const cuadroCrudo = cuadroQ.cuadro
  if (cuadroCrudo && (cuadroCrudo.horaInicio !== jornada.inicio || cuadroCrudo.horaFin !== jornada.fin)) {
    setJornada({ inicio: cuadroCrudo.horaInicio, fin: cuadroCrudo.horaFin })
  }

  const cargasPorNumero = useMemo(() => {
    const m = new Map<string, CargaSalida>()
    for (const c of cargasQ.cargas ?? []) m.set(c.carga, c)
    return m
  }, [cargasQ.cargas])

  const transportistas = useMemo(
    () => [...new Set((cuadroCrudo?.camiones ?? []).map((c) => c.transportista).filter(Boolean) as string[])].sort(),
    [cuadroCrudo],
  )

  /** El cuadro con el filtro aplicado.
   *
   *  Los MUELLES no se filtran nunca: una fila vacía es información —ahí hay
   *  hueco— y esconderla al filtrar por transportista haría creer que ese muelle
   *  no existe. Lo que se filtra son los camiones. */
  const cuadro: CuadroMuelles | undefined = useMemo(() => {
    if (!cuadroCrudo) return undefined
    const term = filtro.busqueda.trim().toLowerCase()
    if (!term && !filtro.estado && !filtro.transportista) return cuadroCrudo

    const cargasDe = (c: CamionMuelle) =>
      c.cargas.map((n) => cargasPorNumero.get(n)).filter((x): x is CargaSalida => Boolean(x))

    const camiones = cuadroCrudo.camiones.filter((c) => {
      if (filtro.transportista && c.transportista !== filtro.transportista) return false
      if (filtro.estado && estadoCamionDe(c, cargasDe(c)) !== filtro.estado) return false
      if (term) {
        const heno = [c.codigo, c.matricula, c.transportista, c.muelle, ...c.cargas]
          .filter(Boolean).join(' ').toLowerCase()
        if (!heno.includes(term)) return false
      }
      return true
    })
    return { ...cuadroCrudo, camiones }
  }, [cuadroCrudo, filtro, cargasPorNumero])

  const [drawer, setDrawer] = useState<{
    abierto: boolean
    camionId: string | null
    slot?: string
    cuando?: Date
    cargaInicial?: string
  }>({ abierto: false, camionId: null })
  const [slotsAbierto, setSlotsAbierto] = useState(false)

  /** El camión VIVO, no el del momento en que se abrió el panel.
   *
   *  Antes el drawer guardaba una copia del camión, así que al engancharle una
   *  carga la lista no cambiaba hasta salir y volver a entrar: la caché se
   *  refrescaba y la copia no. Ahora se busca por id en cada render. */
  const camionAbierto = drawer.camionId
    ? (cuadroCrudo?.camiones.find((c) => c.id === drawer.camionId) ?? null)
    : null

  const slots: SlotMuelle[] = cuadroCrudo?.muelles ?? []
  const cargasDelCamion = camionAbierto
    ? camionAbierto.cargas.map((n) => cargasPorNumero.get(n)).filter((c): c is CargaSalida => Boolean(c))
    : []

  /** Soltar una carga en el carril. Sobre un camión, se le engancha; sobre un
   *  hueco, se abre el alta con la carga y la hora ya puestas. */
  const soltarCarga = (carga: string, destino: { camion?: CamionMuelle; slot: SlotMuelle; cuando: Date }) => {
    if (destino.camion) {
      mut.asignarCarga.mutate(
        { camionId: destino.camion.id, carga },
        { onError: (e) => window.alert(t(`muelles.errores.${errorMuelleDe(e).codigo}`, {
            defaultValue: t('muelles.errores.error_inesperado'),
            ...(errorMuelleDe(e).valores ?? {}),
            camion: errorMuelleDe(e).camion ?? '',
          })) },
      )
      return
    }
    setDrawer({
      abierto: true, camionId: null,
      slot: destino.slot.nombre, cuando: destino.cuando, cargaInicial: carga,
    })
  }

  const pageBg = dark ? '-m-6 p-6 bg-slate-950 text-slate-100 min-h-full' : 'text-slate-900'

  return (
    <div className={pageBg}>
      <PageHeader
        dark={dark}
        title={t('muelles.title')}
        subtitle={t('muelles.subtitle', { almacen, rango: track.label })}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton
              dark={dark}
              onClick={() => { cuadroQ.refetch(); sinMuelleQ.refetch(); cargasQ.refetch() }}
              isLoading={cuadroQ.isFetching}
            />
            <button
              type="button"
              className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
              onClick={() => setDrawer({ abierto: true, camionId: null })}
            >
              {t('muelles.nuevoCamion')}
            </button>
          </div>
        }
      />

      <div className="space-y-3">
        <MuellesFiltros
          almacen={almacen}
          almacenes={ALMACENES}
          onAlmacen={(v) => poner('alm', v)}
          vista={vista}
          onVista={(v) => {
            // El offset cuenta días en la vista de día y semanas en la de
            // semana: conservarlo al cambiar mandaría al usuario a cinco días
            // vista cuando estaba a cinco semanas. Se vuelve a hoy.
            const p = new URLSearchParams(params)
            p.set('vista', v)
            p.delete('off')
            setParams(p, { replace: true })
          }}
          offset={offset}
          onOffset={(n) => poner('off', String(n))}
          etiquetaRango={track.label}
          filtro={filtro}
          onFiltro={setFiltro}
          transportistas={transportistas}
          dark={dark}
          onGestionarSlots={() => setSlotsAbierto(true)}
        />

        {cuadro && (
          <DockKpiStrip
            cuadro={cuadro}
            track={track}
            cargasPorNumero={cargasPorNumero}
            sinMuelle={sinMuelleQ.cargas.length}
            dark={dark}
          />
        )}

        <div className="grid gap-3 lg:grid-cols-[1fr_21rem] items-start">
          <div className="min-w-0">
            {cuadroQ.isLoading || !cuadro ? (
              <Loader label={t('muelles.cargando')} />
            ) : (
              <MuellesTimeline
                cuadro={cuadro}
                track={track}
                cargasPorNumero={cargasPorNumero}
                dark={dark}
                onCamion={(c) => setDrawer({ abierto: true, camionId: c.id })}
                onHueco={(slot, cuando) =>
                  setDrawer({ abierto: true, camionId: null, slot: slot.nombre, cuando })}
                onSoltarCarga={soltarCarga}
              />
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5">
              <LeyendaEstados dark={dark} />
              {/* El pie declara las constantes que gobiernan el eje. Una hora sin
                  zona en una pantalla de expedición no dice nada. */}
              <p className={`text-[10px] tabular-nums ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                {t('muelles.pie', {
                  apertura: `${String(jornada.inicio).padStart(2, '0')}:00`,
                  cierre: `${String(jornada.fin).padStart(2, '0')}:00`,
                  slot: SLOT_MINUTOS,
                  zona: ZONA,
                })}
              </p>
            </div>
          </div>

          <PanelSinMuelle
            cargas={sinMuelleQ.cargas}
            cargando={sinMuelleQ.isLoading}
            dark={dark}
          />
        </div>
      </div>

      <CamionDrawer
        abierto={drawer.abierto}
        camion={camionAbierto}
        slotPorDefecto={drawer.slot}
        cuandoPorDefecto={drawer.cuando}
        cargaInicial={drawer.cargaInicial}
        almacen={almacen}
        slots={slots}
        cargasDisponibles={sinMuelleQ.cargas}
        cargasDelCamion={cargasDelCamion}
        jornada={jornada}
        dark={dark}
        onCerrar={() => setDrawer({ abierto: false, camionId: null })}
      />

      <SlotsDrawer
        abierto={slotsAbierto}
        almacen={almacen}
        slots={slots}
        dark={dark}
        onCerrar={() => setSlotsAbierto(false)}
      />
    </div>
  )
}
